/**
 * Marketing enquiry auto-reply + team alert.
 *
 * When a visitor submits the Contact form or the Request Access / demo modal and
 * the row is persisted to `marketing_submissions` (segment `contact` or
 * `demo_request`), we ping the `notify-lead` edge function so it sends the
 * visitor an auto-reply and alerts the team via Resend. See
 * `supabase/functions/notify-lead/index.ts`.
 *
 * Like `captureLead`, this is fire-and-forget: the enquiry is already saved, so a
 * missing backend or a mail failure must never block the form's success screen.
 * The edge function reads the persisted row as its source of truth (and anti-spam
 * gate), so this only carries the email + segment that key that lookup.
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const FUNCTION = "notify-lead";

/**
 * Ask the backend to send the enquiry auto-reply (visitor) + alert (team).
 *
 * @param {Object} params
 * @param {string} params.email The email the enquiry was submitted under.
 * @param {string} params.segment "contact" or "demo_request".
 * @returns {Promise<{ error: (Error|null) }>}
 */
export async function notifyLead({ email, segment } = {}) {
  if (!email || !segment) {
    return { error: new Error("notifyLead: email and segment are required") };
  }

  if (!isSupabaseConfigured) {
    return { error: null };
  }

  try {
    const { error } = await supabase.functions.invoke(FUNCTION, {
      body: {
        email,
        segment,
        appUrl: typeof window !== "undefined" ? window.location.origin : undefined,
      },
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("notify-lead failed", error.message || error);
    }
    return { error: error || null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("notify-lead threw", err);
    return { error: err };
  }
}
